import React from 'react';
import {
  MAX_MEASURES,
  MEASURES_PER_TRAIN_ROW,
  MEASURE_ACCENTS,
  isMeasureComplete,
  slotsPerMeasure,
  type EcoRitmoSessionConfig,
  type WagonRef,
} from '../../lib/rhythm/ecoRitmoConfig';
import type { RhythmFigureId } from '../../lib/rhythm/rhythmTokens';
import { trainSpriteForSignature } from '../../lib/rhythm/trainSprites';
import { EcoTrainSpriteMeasure } from './EcoTrainSpriteMeasure';
import { EcoWagonSlot } from './EcoWagonSlot';

interface EcoTrainZoneProps {
  config: EcoRitmoSessionConfig;
  selectedWagon: WagonRef;
  onSelectWagon: (ref: WagonRef) => void;
  onSetFigure: (ref: WagonRef, fig: RhythmFigureId | null) => void;
  onMeasureCountChange: (count: number) => void;
}

function MeasureGroup({
  measureIndex,
  config,
  selectedWagon,
  onSelectWagon,
  onSetFigure,
}: {
  measureIndex: number;
  config: EcoRitmoSessionConfig;
  selectedWagon: WagonRef;
  onSelectWagon: (ref: WagonRef) => void;
  onSetFigure: (ref: WagonRef, fig: RhythmFigureId | null) => void;
}) {
  const slots = config.pattern[measureIndex] ?? [];
  const nSlots = slotsPerMeasure(config.timeSignature);
  const complete = isMeasureComplete(slots, config.timeSignature);
  const accent = MEASURE_ACCENTS[measureIndex % MEASURE_ACCENTS.length]!;

  return (
    <div
      className="eco-measure-panel flex flex-col items-center gap-2"
      style={{ ['--measure-accent' as string]: accent }}
    >
      <span className={`eco-measure-label ${complete ? 'eco-measure-label--done' : ''}`}>
        Compás {measureIndex + 1}
      </span>
      <div className="flex items-end justify-center gap-2 sm:gap-3">
        {Array.from({ length: nSlots }).map((_, slotIndex) => (
          <EcoWagonSlot
            key={slotIndex}
            measureIndex={measureIndex}
            slotIndex={slotIndex}
            figure={slots[slotIndex] ?? null}
            selected={
              selectedWagon.measureIndex === measureIndex && selectedWagon.slotIndex === slotIndex
            }
            onSelect={() => onSelectWagon({ measureIndex, slotIndex })}
            onSetFigure={(fig) => onSetFigure({ measureIndex, slotIndex }, fig)}
            variant="default"
          />
        ))}
      </div>
    </div>
  );
}

export const EcoTrainZone: React.FC<EcoTrainZoneProps> = ({
  config,
  selectedWagon,
  onSelectWagon,
  onSetFigure,
  onMeasureCountChange,
}) => {
  const rows = Math.ceil(config.measureCount / MEASURES_PER_TRAIN_ROW);
  const sprite = trainSpriteForSignature(config.timeSignature);

  return (
    <div className="eco-train-zone area-construccion flex-1 min-w-0 flex flex-col min-h-0">
      <div className="eco-zone-toolbar shrink-0 flex items-center justify-between gap-2 px-1 mb-2">
        <span className="eco-zone-title">Zona de construcción</span>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => onMeasureCountChange(config.measureCount - 1)}
            disabled={config.measureCount <= 1}
            className="eco-chip-btn"
            aria-label="Quitar compás"
          >
            −
          </button>
          <span className="eco-chip-count">{config.measureCount}</span>
          <button
            type="button"
            onClick={() => onMeasureCountChange(config.measureCount + 1)}
            disabled={config.measureCount >= MAX_MEASURES}
            className="eco-chip-btn"
            aria-label="Añadir compás"
          >
            +
          </button>
        </div>
      </div>

      <div
        className={`eco-train-stage flex-1 min-h-0 flex flex-col items-stretch relative px-1 ${
          sprite ? 'eco-train-stage--sprites' : 'justify-center gap-4'
        }`}
        style={
          sprite
            ? ({ ['--eco-measure-rows' as string]: String(rows) } as React.CSSProperties)
            : undefined
        }
      >
        {Array.from({ length: rows }).map((_, row) => {
          const start = row * MEASURES_PER_TRAIN_ROW;
          const end = Math.min(start + MEASURES_PER_TRAIN_ROW, config.measureCount);
          return (
            <div key={row} className="eco-train-row w-full flex flex-col items-center min-h-0">
              <div
                className={
                  sprite
                    ? `eco-train-row-sprites eco-train-row-sprites--${end - start}`
                    : 'eco-train-row-track flex flex-wrap items-end justify-center gap-6 sm:gap-10'
                }
              >
                {Array.from({ length: end - start }).map((_, i) => {
                  const measureIndex = start + i;
                  if (sprite) {
                    return (
                      <EcoTrainSpriteMeasure
                        key={measureIndex}
                        measureIndex={measureIndex}
                        config={config}
                        sprite={sprite}
                        selectedWagon={selectedWagon}
                        onSelectWagon={onSelectWagon}
                        onSetFigure={onSetFigure}
                      />
                    );
                  }
                  return (
                    <React.Fragment key={measureIndex}>
                      {i > 0 && (
                        <div className="eco-measure-divider hidden sm:block" aria-hidden />
                      )}
                      <MeasureGroup
                        measureIndex={measureIndex}
                        config={config}
                        selectedWagon={selectedWagon}
                        onSelectWagon={onSelectWagon}
                        onSetFigure={onSetFigure}
                      />
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EcoTrainZone;
